import React, { useState, Fragment, useMemo } from 'react'
import styled from 'styled-components'
import { Col } from 'react-bootstrap'
import { replace } from 'lodash'
import { connect, useDispatch } from 'react-redux'

import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import { Image } from 'react-bootstrap'
import { Input } from './../../../../Components/Form/Input'
import { DateTimePicker } from './../../../../Components/Form/DateTimePicker'
import { SelectDashboard } from 'Components/Form/SelectDashboard'
import { Textarea } from './../../../../Components/Form/Textarea'
import SubmitButton from './../../../../Components/Form/SubmitButton'
import FormButton from './../../../../Components/Form/Button'
import { MissionMap } from './../../../../Components/Missions/MissionMap'
import { Section, SectionLabel, SectionRow } from 'Components/Section'
import ConfirmableTimePicker from './../../../../Components/Form/ConfirmableTimePicker'
import Form from './../../../../Components/Form'

import {
  enableUIBlock,
  disableUIBlock,
} from '../../../../Reducers/UIBlock/actions'
import { authorization } from './../../../../Module/Auth/Auth'
import { getBAName } from '../../../../Utils/User'
import { formatFieldValue } from '../../../../Utils/Formatters'
import {
  convertTimeToTimestamp,
  getEditableFields,
  getTimestampAndSetTime,
  isValidNumericValue,
  missionCheckinCheckoutTimeFormater,
} from './util'
import { number } from 'Utils/Validators/formValidation'
import {
  BRAND_AMBASSADOR_STATUS_ENUM,
  MISSION_ENUM_TYPES,
} from '../../../../shared/constants'
import {
  isLocalDateStr,
  formatToLocalDate,
} from '../../../../shared/libraries/utils/date'
import { showReduxModal } from '../../../../Reducers/UI/actions'

const ProfilePicture = styled(Image)`
  width: auto;
  height: 235px;
`

let warningShow = { date: false, location: false }

const EditMissionDetailsInner = props => {
  const {
    mission,
    city: { currencySign },
    history,
    match: { url },
  } = props
  const { user } = mission

  // Used to update the map when you switch location
  const [location, setLocation] = useState(props.location)
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false)
  const [isValidCheckInOutTime, setIsValidCheckInOutTime] = useState(true)

  const dispatch = useDispatch()

  const shouldShowCurrentPosition =
    authorization.isSpy() ||
    authorization.isCityManager() ||
    authorization.isAdmin()

  const isEmployeeOrSpy =
    authorization.isAdmin() ||
    authorization.isSpy() ||
    authorization.isCityManager()

  if (mission.dateTime && !isLocalDateStr(mission.dateTime))
    throw new Error('Expects dateTime to be a local date.')

  if (mission.checkInTime && !isLocalDateStr(mission.checkInTime))
    throw new Error('Expects checkInTime to be a local date.')

  if (mission.checkoutTime && !isLocalDateStr(mission.checkoutTime))
    throw new Error('Expects checkoutTime to be a local date.')

  const handleCancel = () => {
    history.push(replace(url, '/edit', ''))
  }

  const updateMission = async form => {
    props.dispatch(enableUIBlock())

    //convert data from form to updateMission format
    const convertedFormData = Object.entries(form).reduce(
      (acc, [fieldName, { value }]) => {
        acc[fieldName] = value
        return acc
      },
      {},
    )

    try {
      const newData = {
        ...convertedFormData,
        id: mission.id,
        draftPayAdjustment: isValidNumericValue(
          convertedFormData.draftPayAdjustment,
        )
          ? Number(convertedFormData.draftPayAdjustment)
          : undefined,
        payRatio: isValidNumericValue(convertedFormData.payRatio)
          ? Number(convertedFormData.payRatio)
          : undefined,
        checkoutFlyerQuantity: isValidNumericValue(
          convertedFormData.checkoutFlyerQuantity,
        )
          ? Number(convertedFormData.checkoutFlyerQuantity)
          : undefined,
        checkInTime:
          mission.checkInTime == convertedFormData.checkInTime
            ? undefined
            : missionCheckinCheckoutTimeFormater(
                mission.dateTime,
                convertedFormData.checkInTime,
              ),
        checkoutTime:
          mission.checkoutTime == convertedFormData.checkoutTime
            ? undefined
            : missionCheckinCheckoutTimeFormater(
                mission.dateTime,
                convertedFormData.checkoutTime,
              ),
      }
      await props.updateMission(newData)
      // maybe fix for task https://www.pivotaltracker.com/story/show/175512420
    } catch (err) {
      console.error(err)
    } finally {
      props.dispatch(disableUIBlock())
    }
  }

  const editableFields = getEditableFields(mission.status, mission.type)

  const brandAmbassadorsSelectСache = useMemo(
    () => (
      <SelectDashboard
        name="userId"
        options={props.brandAmbassadors
          .filter(item => item.status == BRAND_AMBASSADOR_STATUS_ENUM.ACTIVE)
          .map(c => ({
            label: c.name,
            value: c.id,
          }))}
        input={{
          value: mission.userId,
        }}
      />
    ),
    [props.brandAmbassadors, mission.userId],
  )

  const missionMapCache = useMemo(
    () => (
      <MissionMap
        mission={{
          ...mission,
          location,
        }}
        shouldShowCurrentPosition={shouldShowCurrentPosition}
      />
    ),
    [mission, location, shouldShowCurrentPosition, mission.activities],
  )

  const handleFormUpdate = (form, formState) => {
    const missionAvailableTimestamp = convertTimeToTimestamp(mission.dateTime)
    const missionCheckInTimestamp = convertTimeToTimestamp(mission.checkInTime)
    const checkInTimeFormValue =
      (formState.checkInTime && formState.checkInTime.value) || null
    const checkOutTimeFormValue =
      (formState.checkoutTime && formState.checkoutTime.value) || null

    if (
      checkInTimeFormValue ||
      (missionCheckInTimestamp && checkOutTimeFormValue)
    ) {
      const checkInTimeStamp = checkInTimeFormValue
        ? getTimestampAndSetTime({
            missionDateTime: mission.checkInTime,
            hoursStr: checkInTimeFormValue,
          })
        : missionCheckInTimestamp

      const checkoutTimeStamp = checkOutTimeFormValue
        ? getTimestampAndSetTime({
            missionDateTime: mission.checkInTime,
            hoursStr: checkOutTimeFormValue,
          })
        : missionAvailableTimestamp

      setIsValidCheckInOutTime(checkInTimeStamp < checkoutTimeStamp)
    }
    setSubmitButtonDisabled(!!(isValidCheckInOutTime && form.isValid))

    if (mission.canBeSpied) {
      if (
        formState.dateTime &&
        formState.dateTime.value !== mission.dateTime &&
        !warningShow.date
      ) {
        warningShow = { ...warningShow, date: true }
        setTimeout(() => {
          dispatch(
            showReduxModal({
              title: 'Warning',
              children:
                'The mission would be removed from the spy shift if you change date',
              confirmLabel: 'OK',
              hideCancel: true,
            }),
          )
        }, 300)
      }

      if (
        formState.locationId &&
        formState.locationId.value !== props.location.id &&
        !warningShow.location
      ) {
        warningShow = { ...warningShow, location: true }

        setTimeout(() => {
          dispatch(
            showReduxModal({
              title: 'Warning',
              children:
                'The mission would be removed from the spy shift if you change location',
              confirmLabel: 'OK',
              hideCancel: true,
            }),
          )
        }, 300)
      }
    }
  }

  return (
    <Fragment>
      <Form onUpdate={handleFormUpdate} onSubmit={updateMission}>
        <Section title={'Mission Details'}>
          <SectionRow>
            <Col xs={4}>
              <SectionLabel>Date</SectionLabel>
              {editableFields.includes('dateTime') ? (
                <DateTimePicker
                  name="dateTime"
                  initValue={mission.dateTime}
                  input={{
                    value: mission.dateTime,
                  }}
                />
              ) : (
                <span>{formatToLocalDate(new Date(mission.dateTime))}</span>
              )}
            </Col>
            <Col xs={4}>
              <SectionLabel>Pack Size</SectionLabel>
              <span>{mission.packSize}</span>
            </Col>
            <Col xs={4}>
              <SectionLabel>Flyers Distributed</SectionLabel>
              {editableFields.includes('checkoutFlyerQuantity') ? (
                <Input
                  value={mission.totalFlyersDistributed}
                  initValue={mission.totalFlyersDistributed}
                  name="checkoutFlyerQuantity"
                  validator={[number]}
                />
              ) : (
                <span>{mission.checkoutFlyerQuantity}</span>
              )}
            </Col>
          </SectionRow>
          <SectionRow>
            <Col xs={4}>
              <SectionLabel>Flyer Code</SectionLabel>
              {props.flyerCodes.length == 0 ? (
                <span style={{ lineHeight: '50px', opacity: 0.8 }}>
                  No available codes
                </span>
              ) : editableFields.includes('flyerInventoryId') ? (
                <SelectDashboard
                  options={props.flyerCodes.map(c => ({
                    label: c.code,
                    value: c.id,
                  }))}
                  input={{
                    value: mission.flyerInventoryId,
                  }}
                  initValue={mission.flyerInventoryId}
                  name="flyerInventoryId"
                />
              ) : (
                <span>
                  {mission.flyerInventoryId
                    ? props.flyerCodes.find(
                        f => f.id == mission.flyerInventoryId,
                      ).code
                    : '-'}
                </span>
              )}
            </Col>
            <Col xs={4}>
              <SectionLabel>Location</SectionLabel>
              {!mission.locationId ? (
                <span>-</span>
              ) : editableFields.includes('locationId') ? (
                <SelectDashboard
                  options={props.locations.map(c => ({
                    label: c.name,
                    value: c.id,
                  }))}
                  onChange={(e, k, v) => {
                    return setLocation(props.locations.find(l => l.id == v))
                  }}
                  input={{
                    value: mission.locationId,
                  }}
                  initValue={mission.locationId}
                  name="locationId"
                />
              ) : (
                <span>
                  {props.locations.find(l => l.id == mission.locationId).name}
                </span>
              )}
            </Col>
            <Col xs={4}>
              <SectionLabel>Ambassador</SectionLabel>
              {editableFields.includes('userId') ? (
                brandAmbassadorsSelectСache
              ) : (
                <span>
                  {
                    props.brandAmbassadors.find(ba => ba.id == mission.userId)
                      .name
                  }
                </span>
              )}
            </Col>
          </SectionRow>
          <SectionRow>
            <Col xs={12}>
              <SectionLabel>Comment</SectionLabel>
              {editableFields.includes('comments') ? (
                <Textarea
                  name="comments"
                  initValue={mission.comments}
                  meta={{}}
                />
              ) : (
                <span>{mission.comments}</span>
              )}
            </Col>
          </SectionRow>
          <SectionRow>
            <Col xs={4}>
              <SectionLabel>Pay ratio (multiplier)</SectionLabel>
              {editableFields.includes('payoutRatio') ? (
                <Input
                  name="payoutRatio"
                  value={mission.payoutRatio}
                  initValue={mission.payoutRatio}
                  validator={[number]}
                />
              ) : (
                <span>{mission.payRatio}</span>
              )}
            </Col>
            <Col xs={8}>
              <SectionLabel>Pay ratio reason</SectionLabel>
              {editableFields.includes('payoutRatioComment') ? (
                <Textarea
                  name="payoutRatioComment"
                  initValue={mission.payoutRatioComment}
                  meta={{}}
                />
              ) : (
                <span>{mission.payoutRatioComment}</span>
              )}
            </Col>
          </SectionRow>
          {mission.type === MISSION_ENUM_TYPES.LETTERBOX && (
            <SectionRow>
              <Col xs={4}>
                <SectionLabel>Addresses</SectionLabel>
                <span>{formatFieldValue(mission.householdsAmount)}</span>
              </Col>
              <Col xs={4}>
                <SectionLabel>Density</SectionLabel>
                <span>{formatFieldValue(mission.density)}</span>
              </Col>
            </SectionRow>
          )}
          <SectionRow>
            {!mission.costPerFlyer && (
              <Col xs={4}>
                <SectionLabel>Pay аdjustment ({currencySign})</SectionLabel>
                {editableFields.includes('draftPayAdjustment') ? (
                  <Input
                    name="draftPayAdjustment"
                    value={mission.draftPayAdjustment / 100}
                    initValue={mission.draftPayAdjustment / 100}
                    validator={[number]}
                  />
                ) : (
                  <span>{mission.draftPayAdjustment / 100}</span>
                )}
              </Col>
            )}
          </SectionRow>
          <SectionRow>
            <Col xs={12}>{mission.id && missionMapCache}</Col>
          </SectionRow>
        </Section>
        {mission.userId && (
          <Fragment>
            <Section title={'Brand Ambassador'}>
              <SectionRow>
                <Col xs={3}>
                  <ProfilePicture src={user.profilePicture} />
                </Col>
                <Col xs={9}>
                  <Box pb={2}>
                    <SectionLabel>Name</SectionLabel>
                    {getBAName({
                      firstName: user.firstName,
                      lastName: user.lastName,
                      isTeamLeader: user.isTeamLeader,
                    })}
                  </Box>

                  <Box pb={2}>
                    <SectionLabel>CheckIn Time</SectionLabel>
                    {editableFields.includes('checkInTime') ? (
                      <div
                        style={
                          !isValidCheckInOutTime
                            ? { borderBottom: '1px solid red' }
                            : {}
                        }
                      >
                        <ConfirmableTimePicker
                          name="checkInTime"
                          pickerName="Check In Time"
                          value={
                            mission.checkInTime
                              ? mission.checkInTime.slice(11, 16)
                              : undefined
                          }
                          minutesStep={1}
                        />
                      </div>
                    ) : (
                      <span>
                        {mission.checkInTime
                          ? mission.checkInTime.slice(11, 16)
                          : '-'}
                      </span>
                    )}
                    {!isValidCheckInOutTime ? (
                      <span
                        style={{
                          color: 'red',
                          marginTop: '4px',
                          display: 'block',
                        }}
                      >
                        Check In Time time should be less then Checkout Time
                      </span>
                    ) : null}
                  </Box>

                  <Box pb={2}>
                    <SectionLabel>Checkout Time</SectionLabel>
                    {editableFields.includes('checkoutTime') ? (
                      <div
                        style={
                          !isValidCheckInOutTime
                            ? { borderBottom: '1px solid red' }
                            : {}
                        }
                      >
                        <ConfirmableTimePicker
                          name="checkoutTime"
                          pickerName="Checkout Time"
                          value={
                            mission.checkoutTime
                              ? mission.checkoutTime.slice(11, 16)
                              : undefined
                          }
                          minutesStep={1}
                        />
                      </div>
                    ) : (
                      <span>
                        {mission.checkoutTime
                          ? mission.checkoutTime.slice(11, 16)
                          : '-'}
                      </span>
                    )}
                    {!isValidCheckInOutTime ? (
                      <span
                        style={{
                          color: 'red',
                          marginTop: '4px',
                          display: 'block',
                        }}
                      >
                        Checkout Time time should be gather then Check In Time
                      </span>
                    ) : null}
                  </Box>

                  <Box pb={2}>
                    <SectionLabel>Pay Adjustment</SectionLabel>
                    {mission.payoutRatio !== null
                      ? `(${mission.payoutRatio}) Reason: ${
                          mission.payoutRatioComment
                        }`
                      : '-'}
                  </Box>

                  {isEmployeeOrSpy && props.onNotifyMissionWasSpied && (
                    <Box pb={2}>
                      {mission.notifiedSuccessfullySpied ? (
                        'Notified successfully spied ✅'
                      ) : (
                        <Button
                          variant="contained"
                          onClick={props.onNotifyMissionWasSpied}
                          //disabled={isLoading} TODO
                        >
                          Notify successfully spied
                        </Button>
                      )}
                    </Box>
                  )}
                </Col>
              </SectionRow>
            </Section>
          </Fragment>
        )}
        <Box pb={5} display="flex" justifyContent="space-between">
          <FormButton value="Cancel" onClick={handleCancel} />
          <SubmitButton disabled={!submitButtonDisabled} value="Save" />
        </Box>
      </Form>
    </Fragment>
  )
}

const MissionLoadingWrapper = ({ missionDataLoaded, ...otherProps }) =>
  missionDataLoaded ? (
    <EditMissionDetailsInner {...otherProps} />
  ) : (
    'Loading mission...'
  )

export default connect()(MissionLoadingWrapper)



// WEBPACK FOOTER //
// ./src/Containers/Form/Mission/EditMissionDetails/View.js